import {errorLogger} from './errorLogger'
import pWaterfall from 'p-waterfall'
import {DebuggerMode, Mode} from './shared/DebuggerMode'
import {DebuggerConsole} from './shared/DebuggerConsole'
import {sensitiveDataCleanerMiddleware} from './middlewares/sensitiveDataCleanerMiddleware'
import {getEnv} from './shared/env'
import {withNamespace} from './shared/withNamespace'
import {sessionStore} from './shared/SessionStore'

console.debug(`[vendorevent] [${new Date().toISOString()}]:`, 'init')

if (getEnv().LOGGER_DISABLE_VENDOREVENT) {
  console.debug(`[vendorevent] [${new Date().toISOString()}]:`, 'actual_event_send_disabled_by_env')
}

type Log = {
  event: string
  level?: 'warn' | 'error' | 'debug' | 'info' | 'fatal'
  data?: unknown
}

type Event = {
  event: string
  level: 'warn' | 'error' | 'debug' | 'info' | 'fatal'
  timestamp: string
  data?: unknown
  env: Record<string, unknown>
}

const MAX_BATCH_SIZE = 10
const MAX_QUEUE_SIZE = 100
const MAX_BATCH_SIZE_KB = 1_000
const BATCHING_INTERVAL_MS = 1_500

const debuggerMode = new DebuggerMode({key: '_evt_debug'})
const debuggerConsole = new DebuggerConsole({
  badge: 'vendorevent',
  badgeColor: '#fff',
  badgeBgColor: 'hsl(100deg, 40%, 30%)',
  collapse: debuggerMode.getMode() === Mode.ENABLED_COLLAPSE
})

/**
 * Ссылка куда будут попадать логи:\
 * https://elk7.eda.yandex.net/goto/9907317595a3ea6975c05ddcb11bf140
 *
 * Чтобы включить вывод в консоль, надо открыть страницу с параметром `_evt_debug=1` или `_evt_debug=2`\
 * Например: https://vendor.yandex.ru/home?_evt_debug=1, для отключения `_evt_debug=0`
 */
export const vendorEvent = withNamespace(
  createLogger(),
  (namespace, log) => [{...log, event: [namespace, log.event].filter(Boolean).join(':')}] as const,
  ':'
)

function createLogger() {
  const queue: Event[] = []

  nextBatch()
  function nextBatch() {
    setTimeout(() => {
      if (queue.length < 1) return nextBatch()
      const batch = queue.splice(0, MAX_BATCH_SIZE)
      sendVendorEvent(batch).catch(onError).finally(nextBatch)
    }, BATCHING_INTERVAL_MS)
  }

  return (log: Log) =>
    void pWaterfall(
      [
        async (prev) =>
          Object.assign(prev, {
            level: prev.level || 'info',
            timestamp: new Date().toISOString(),
            data: prev.data ?? 'no_data',
            env: {
              env: sessionStore.data.env,
              requestId: sessionStore.data.requestId,
              sessionId: sessionStore.data.sessionId,
              parentRequestId: sessionStore.data.parentRequestId,
              service: sessionStore.data.service,
              version: sessionStore.data.version,
              partnerId: sessionStore.data.partnerId
            }
          }),
        sensitiveDataCleanerMiddleware<Event>,
        (prev) => {
          if (queue.length + 1 >= MAX_QUEUE_SIZE) {
            throw new Error(`Max queue size (${MAX_QUEUE_SIZE}) exceeded`)
          }

          queue.push(prev)
        }
      ],
      log
    ).catch(onError)
}

async function sendVendorEvent(event: Event[]) {
  const firstEvent = event[0]
  if (!firstEvent) {
    return
  }

  const body = new Blob([JSON.stringify({logs: event})], {type: 'application/json'})
  const bodySizeKB = (body.size / 1_000).toFixed(2)

  if (+bodySizeKB >= MAX_BATCH_SIZE_KB) {
    throw new Error(`Body size more than ${MAX_BATCH_SIZE_KB}KB`)
  }

  if (debuggerMode.getMode() === Mode.ENABLED || debuggerMode.getMode() === Mode.ENABLED_COLLAPSE) {
    debuggerConsole.createGroup(
      `length:${event.length} size:${bodySizeKB}kB`,
      `${firstEvent.level || 'no_level'} ${firstEvent.event || 'no_name'}`
    )

    event.forEach((log) => {
      debuggerConsole.log(`${firstEvent.level || 'no_level'} ${firstEvent.event || 'no_name'}`, log)
    })

    debuggerConsole.endGroup()
  }

  if (getEnv().LOGGER_DISABLE_VENDOREVENT) return

  const endpoint = '/web-api/log/vendorevent'

  if (typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(endpoint, body)
    return
  }

  await fetch(endpoint, {
    method: 'POST',
    mode: 'same-origin',
    keepalive: true,
    headers: {'Content-Type': 'application/json'},
    body: body
  })
}

function onError(error: unknown) {
  const safeError = error instanceof Error ? error : new Error(`NonError: ${error}`)
  errorLogger({error: safeError, message: 'VendorEvent'})
  console.error(error)
}
